import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { HeroUIProvider } from './providers/HeroUIProvider';
import { QueryProvider } from './providers/QueryProvider';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import ThemeInitializer from './components/ThemeInitializer';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Ingredientes from './pages/Ingredientes';
import Lotes from './pages/Lotes';
import Movimientos from './pages/Movimientos';
import Reportes from './pages/Reportes';
import ReporteExistencias from './pages/reportes/ReporteExistencias';
import ReporteConciliacion from './pages/reportes/ReporteConciliacion';
import ReporteLotes from './pages/reportes/ReporteLotes';
import ReporteCompras from './pages/reportes/ReporteCompras';
import ReporteConteo from './pages/reportes/ReporteConteo';
import ReporteImprimir from './pages/reportes/ReporteImprimir';
import Mermas from './pages/Mermas';
import Recetas from './pages/Recetas';
import MinutasNuevas from './pages/MinutasNuevas';
import EditorMinuta from './pages/EditorMinuta';
import ArmarPedido from './pages/ArmarPedido';
import PedidosMain from './pages/PedidosMain';
import DetallePedido from './pages/DetallePedido';
import ClientesClean from './pages/ClientesClean';
import CocinaRegistros from './pages/CocinaRegistros';
import CocinaTandas from './pages/CocinaTandas';
import Despacho from './pages/Despacho';
import DespachoImprimir from './pages/DespachoImprimir';
import Compras from './pages/Compras';
import Proveedores from './pages/Proveedores';
import PermissionsTest from './pages/PermissionsTest';
import Administracion from './pages/Administracion';
import TrainingLanding from './pages/capacitacion/TrainingLanding';
import TrainingModulos from './pages/capacitacion/TrainingModulos';
import TrainingModuloDetalle from './pages/capacitacion/TrainingModuloDetalle';
import TrainingQuiz from './pages/capacitacion/TrainingQuiz';
import TrainingResultado from './pages/capacitacion/TrainingResultado';
import TrainingAdminDashboard from './pages/capacitacion/TrainingAdminDashboard';
import { ToastProvider } from './hooks/useToast';
import { ROLES, PERMISSIONS } from './hooks/usePermissions';

// Componente para manejar la redirección inteligente
function RedirectRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  console.log('🏠 [RedirectRoute] Estado de redirección:', {
    isAuthenticated,
    isLoading,
    action: isLoading
      ? 'Loading'
      : isAuthenticated
        ? 'Redirect to dashboard'
        : 'Redirect to login',
  });

  // Mostrar loading mientras se verifica la autenticación
  if (isLoading) {
    console.log(
      '⏳ [RedirectRoute] Mostrando loading para verificación de autenticación'
    );
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  // Redirigir según el estado de autenticación
  if (isAuthenticated) {
    console.log(
      '✅ [RedirectRoute] Usuario autenticado, redirigiendo a /dashboard'
    );
    return <Navigate to="/dashboard" replace />;
  } else {
    console.log(
      '❌ [RedirectRoute] Usuario no autenticado, redirigiendo a /login'
    );
    return <Navigate to="/login" replace />;
  }
}

// Componente interno que maneja el tema
function AppContent() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <ThemeInitializer />
      <Routes>
        {/* Ruta pública - Login */}
        <Route path="/login" element={<Login />} />

        {/* Rutas protegidas con Layout */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Ruta por defecto - redirigir según autenticación */}
        <Route path="/" element={<RedirectRoute />} />

        {/* Rutas adicionales protegidas con Layout */}
        <Route
          path="/recetas"
          element={
            <ProtectedRoute
              allowedRoles={[ROLES.CHEF, ROLES.ADMINISTRADOR]}
              requiredPermission={PERMISSIONS.RECETAS_READ}
            >
              <Layout>
                <Recetas />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Fix tanda2 T2-08 (2026-06-30): la ruta /minutas quedó
            deshabilitada al migrar al sistema nuevo → los links viejos y
            favoritos daban 404. Redirigimos a /minutas-nuevas para
            preservar continuidad sin restaurar el módulo viejo. */}
        <Route
          path="/minutas"
          element={<Navigate to="/minutas-nuevas" replace />}
        />

        {/* NUEVA RUTA DE MINUTAS - SISTEMA ACTUALIZADO */}
        <Route
          path="/minutas-nuevas"
          element={
            <ProtectedRoute
              allowedRoles={[ROLES.CHEF, ROLES.ADMINISTRADOR]}
              requiredPermission={PERMISSIONS.MINUTAS_READ}
            >
              <Layout>
                <MinutasNuevas />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* EDITOR DE MINUTAS */}
        <Route
          path="/minutas-nuevas/:id/editar"
          element={
            <ProtectedRoute
              allowedRoles={[ROLES.CHEF, ROLES.ADMINISTRADOR]}
              requiredPermission={PERMISSIONS.MINUTAS_READ}
            >
              <Layout>
                <EditorMinuta />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Nuevas rutas */}
        <Route
          path="/ingredientes"
          element={
            <ProtectedRoute
              allowedRoles={[
                ROLES.CHEF,
                ROLES.ADMINISTRADOR,
                ROLES.PERSONAL_COCINA,
              ]}
              requiredPermission={PERMISSIONS.INGREDIENTES_READ}
            >
              <Layout>
                <Ingredientes />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/lotes"
          element={
            <ProtectedRoute allowedRoles={['chef', 'administrador']}>
              <Layout>
                <Lotes />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Kardex global de movimientos (PROMPT_N°053). Mismos roles que
            Lotes: el Encargado de Inventario tiene rol `chef`. */}
        <Route
          path="/movimientos"
          element={
            <ProtectedRoute allowedRoles={['chef', 'administrador']}>
              <Layout>
                <Movimientos />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/ordenes-compra"
          element={<Navigate to="/compras" replace />}
        />

        {/* La pantalla mock de reportes de compras se borró (N°054); lo
            que mostraba ahora vive en /reportes/compras, con datos reales. */}
        <Route
          path="/reportes-compras"
          element={<Navigate to="/reportes/compras" replace />}
        />

        <Route
          path="/sugerencias-compras"
          element={<Navigate to="/compras" replace />}
        />

        {/* Reportes de inventario (N°054). La vista de impresión va fuera
            del Layout a propósito: la barra lateral no debe salir en el
            papel, igual que en /despacho/imprimir. */}
        <Route
          path="/reportes"
          element={
            <ProtectedRoute allowedRoles={['chef', 'administrador']}>
              <Layout>
                <Reportes />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reportes/existencias"
          element={
            <ProtectedRoute allowedRoles={['chef', 'administrador']}>
              <Layout>
                <ReporteExistencias />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reportes/conciliacion"
          element={
            <ProtectedRoute allowedRoles={['chef', 'administrador']}>
              <Layout>
                <ReporteConciliacion />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reportes/lotes"
          element={
            <ProtectedRoute allowedRoles={['chef', 'administrador']}>
              <Layout>
                <ReporteLotes />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reportes/compras"
          element={
            <ProtectedRoute allowedRoles={['chef', 'administrador']}>
              <Layout>
                <ReporteCompras />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reportes/conteo"
          element={
            <ProtectedRoute allowedRoles={['chef', 'administrador']}>
              <Layout>
                <ReporteConteo />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reportes/:nombre/imprimir"
          element={
            <ProtectedRoute allowedRoles={['chef', 'administrador']}>
              <ReporteImprimir />
            </ProtectedRoute>
          }
        />

        <Route
          path="/cocina"
          element={
            <ProtectedRoute
              allowedRoles={['personal_cocina', 'chef', 'administrador']}
            >
              <Layout>
                <CocinaRegistros />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Fase 6 (2026-07-01): pantalla de tandas segmentadas día/noche */}
        <Route
          path="/cocina-tandas"
          element={
            <ProtectedRoute
              allowedRoles={['personal_cocina', 'chef', 'administrador']}
            >
              <Layout>
                <CocinaTandas />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Módulo Despacho — entre Cocina y Mermas */}
        <Route
          path="/despacho"
          element={
            <ProtectedRoute
              allowedRoles={['personal_cocina', 'chef', 'administrador', 'cliente']}
            >
              <Layout>
                <Despacho />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/despacho/imprimir/cliente/:clienteId"
          element={
            <ProtectedRoute
              allowedRoles={['personal_cocina', 'chef', 'administrador', 'cliente']}
            >
              <DespachoImprimir />
            </ProtectedRoute>
          }
        />
        <Route
          path="/despacho/imprimir/operacion/:clienteId/:operacionId"
          element={
            <ProtectedRoute
              allowedRoles={['personal_cocina', 'chef', 'administrador', 'cliente']}
            >
              <DespachoImprimir />
            </ProtectedRoute>
          }
        />

        {/* GESTIÓN DE PEDIDOS - Sistema con Tabs */}
        <Route
          path="/pedidos"
          element={
            <ProtectedRoute>
              <Layout>
                <PedidosMain />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* DETALLE DE PEDIDO */}
        <Route
          path="/pedidos/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <DetallePedido />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* NUEVA RUTA: Armar Pedido (basado en nuevas minutas) */}
        <Route
          path="/armar-pedido"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CLIENTE, ROLES.ADMINISTRADOR]}>
              <Layout>
                <ArmarPedido />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/clientes"
          element={
            <ProtectedRoute allowedRoles={['administrador']}>
              <Layout>
                <ClientesClean />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/mermas"
          element={
            <ProtectedRoute allowedRoles={['chef', 'administrador']}>
              <Layout>
                <Mermas />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/compras"
          element={
            <ProtectedRoute allowedRoles={['chef', 'administrador']}>
              <Layout>
                <Compras />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/proveedores"
          element={
            <ProtectedRoute allowedRoles={['chef', 'administrador']}>
              <Layout>
                <Proveedores />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Rutas de Administración - solo administradores */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['administrador']}>
              <Layout>
                <Administracion />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Rutas legacy - redirigir a admin */}
        <Route
          path="/admin/usuarios"
          element={<Navigate to="/admin" replace />}
        />

        <Route path="/admin/roles" element={<Navigate to="/admin" replace />} />

        {/* Ruta de test de permisos - solo para desarrollo */}
        <Route
          path="/permissions-test"
          element={
            <ProtectedRoute>
              <Layout>
                <PermissionsTest />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* === Módulo de Capacitación === */}
        {/* Rutas públicas (sin ProtectedRoute - sesión propia en localStorage) */}
        <Route path="/capacitacion" element={<TrainingLanding />} />
        <Route path="/capacitacion/modulos" element={<TrainingModulos />} />
        <Route path="/capacitacion/modulo/:slug" element={<TrainingModuloDetalle />} />
        <Route path="/capacitacion/prueba/:attemptId" element={<TrainingQuiz />} />
        <Route path="/capacitacion/resultado/:attemptId" element={<TrainingResultado />} />

        {/* Admin reusa el JWT existente + rol administrador */}
        <Route
          path="/capacitacion/admin"
          element={
            <ProtectedRoute allowedRoles={['administrador']}>
              <Layout>
                <TrainingAdminDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/capacitacion/admin/dashboard"
          element={<Navigate to="/capacitacion/admin" replace />}
        />

        {/* Ruta 404 */}
        <Route
          path="*"
          element={
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                <p className="text-gray-600 mb-4">Página no encontrada</p>
                <a
                  href="/dashboard"
                  className="text-blue-600 hover:text-blue-500 font-medium"
                >
                  Volver al Dashboard
                </a>
              </div>
            </div>
          }
        />
      </Routes>
    </div>
  );
}

// Componente principal de la aplicación
function App() {
  return (
    <QueryProvider>
      <HeroUIProvider>
        <AuthProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </AuthProvider>
      </HeroUIProvider>
    </QueryProvider>
  );
}

export default App;
